/*
Copyright 2022 Alexandra Instituttet

Der er givet en begrænset licens af denne software til GEO. Licensen
giver dem ret til at benytte, videreudvikle og fejlrette udelukkende
med henblik på brug i GeoAtlasStudy web-applikationen.
*/
/* eslint-disable no-unused-vars, default-case */

import {mat4, vec3} from 'gl-matrix';
import {load}       from '@loaders.gl/core';

import {Tiles3DLoader} from '@loaders.gl/3d-tiles';

// Fundament
import {WOrientedBox, WSphere, wVec3Basics} from 'wfundament';

export const wTileTypes = {
  ModelUnknown: -1,
  BatchedModel: 0,
  InstancedModel: 1,
  PointModel: 2,
  CompositeModel: 3,
  JsonModel: 4,
};

export const wTileRefineStrategy = {
  RefineUnknown: -1,
  RefineReplace: 0,
  RefineAdd: 1,
};

class W3DTile {
  constructor(json, resourcePath, parentRefineStrategy, parentTileIndex) {
    this.resourcePath_ = resourcePath;

    // json
    this.json_ = json;
    this.content_ = json.content;
    this.boundingVolumeJson_ = json.boundingVolume;
    this.refineStrategy_ = json.refine ? json.refine : parentRefineStrategy;

    if (this.content_ && this.content_.uri) {
      this.tileDataUrl_ = this.content_.uri
        ? this.content_.uri
        : this.content_.url;
      this.tileType_ = this.findTileType(this.tileDataUrl_);
    }

    // Rendering info
    this.geometricError_ = json.geometricError;
    this.transform_ = mat4.create();

    // Feature and batch tables

    // bounding volume
    this.hasBoundingSphere_ = false;
    this.hasBoundingObb_ = false;
    this.createBoundingVolume();

    // caching info
    this.cacheIndex_ = -1;
    this.isLoaded_ = false;
    this.isVisible_ = false; // frustum culling

    // tree information
    this.parentIndex_ = parentTileIndex;
    this.childInidices_ = [];

    // debug
    this.vertexData_ = null;
    this.indexData_ = null;
  }

  getName() {
    return this.tileDataUrl_;
  }

  addChildIndex(childIndex) {
    this.childInidices_.push(childIndex);
  }

  setGeometricError(geometricError) {
    this.geometricError_ = geometricError;
  }

  getRefineStrategy() {
    return this.refineStrategy_;
  }

  createBoundingVolume() {
    this.obb_ = new WOrientedBox(
      "TileObb",
      vec3.fromValues(0.0, 0.0, 0.0),
      wVec3Basics.X,
      wVec3Basics.Y,
      wVec3Basics.Z
    );
    this.boundSphere_ = new WSphere(vec3.fromValues(0.0, 0.0, 0.0), 1.0);
  }

  findTileType(url) {
    if (!url) {
      return wTileTypes.ModelUnknown;
    }
    if (url.substr(0, 4) !== "http") {
      url = this.resourcePath_ + url;
    }

    this.dataUrl_ = url;
    const type = url.slice(-4);
    switch (type) {
      case "b3dm":
        return wTileTypes.BatchedModel;

      case "json":
        return wTileTypes.JsonModel;

      case "pnts":
        return wTileTypes.PointModel;

      case "i3dm":
        return wTileTypes.InstancedModel;

      case "cmpt":
        return wTileTypes.CompositeModel;
    }
    return wTileTypes.ModelUnknown;
  }

  async loadTileData() {
    switch (this.tileType_) {
      case wTileTypes.BatchedModel:
        await this.loadB3dmTile(this.dataUrl_);
        break;
      case wTileTypes.InstancedModel:
        break;
      case wTileTypes.PointModel:
        break;
      case wTileTypes.CompositeModel:
        break;
    }
  }

  async loadB3dmTile(url) {
    let gltf = await load(url, Tiles3DLoader);
    const mesh = gltf.gltf.meshes[0];

    let positionData = null;
    let colorData = null;
    let normalData = null;
    let batchData = null;

    const vertexCount = 0;

    let vertexType = "vertex3";
    let stride = 3;
    if (mesh.primitives[0].attributes.POSITION) {
      positionData = mesh.primitives[0].attributes.POSITION.value;
    }

    let hasNormals = false;
    if (mesh.primitives[0].attributes.NORMAL) {
      normalData = mesh.primitives[0].attributes.NORMAL.value;
      stride += 3;
      hasNormals = true;
      vertexType += "Normal3";
    }
    let hasColors = false;
    if (mesh.primitives[0].attributes.COLOR_0) {
      colorData = mesh.primitives[0].attributes.COLOR_0.value;
      stride += 3;
      hasColors = true;
      vertexType += "Color3";
    }

    let hasBatchIDs = false;
    if (mesh.primitives[0].attributes._BATCHID) {
      batchData = mesh.primitives[0].attributes._BATCHID.value;
      stride += 1;
      hasBatchIDs = true;
      vertexType += "BatchID1";
    }

    this.vertexType_ = vertexType;

    mat4.copy(this.transform_, gltf.gltf.nodes[0].matrix);

    let batchid = null;
    if (hasBatchIDs) {
      batchid = new Uint32Array(gltf.batchTableBinary.buffer);
    }

    //  let offsetX = gltf.gltf.scene.nodes[0].matrix[12];
    // let offsetY = gltf.gltf.scene.nodes[0].matrix[14];
    //        let stride = 7 // [x, y, z, nx, ny, nz, batchid]
    const vertices = positionData.length / 3;
    const normOffset = 3;
    const triangles = vertices / 3;
    this.vertexData_ = new Float32Array(stride * vertices);
    //  let u32vboview = new Uint32Array(vbodata.buffer);
    for (let i = 0; i < vertices; i++) {
      let j = stride * i;
      this.vertexData_[j++] = positionData[3 * i];
      this.vertexData_[j++] = positionData[3 * i + 1];
      this.vertexData_[j++] = positionData[3 * i + 2];

      if (hasNormals) {
        this.vertexData_[j++] = normalData[3 * i];
        this.vertexData_[j++] = normalData[3 * i + 1];
        this.vertexData_[j++] = normalData[3 * i + 2];
      }

      if (hasColors) {
        this.vertexData_[j++] = colorData[3 * i];
        this.vertexData_[j++] = colorData[3 * i + 1];
        this.vertexData_[j++] = colorData[3 * i + 2];
      }

      if (hasBatchIDs) {
        this.vertexData_[j++] = batchid[batchData[i]];
      }

      //  u32vboview[stride*i + 6] = batchid[Math.floor(batchptr[i])];
    }

    /*  meshdata.matrixWorld = mat4.create();

     //console.log(vbodata);
     let v = [vec3.create(), vec3.create(), vec3.create()];
     let e1 = vec3.create();
     let e2 = vec3.create();
     let nn = vec3.create();
     let n = vec3.create();
     for ( let i = 0; i < triangles; i++ )
     {
     for ( let j = 0; j < 3; j++ )
     {
     //vec3.set(v[j], pos[9*i+3*j], pos[9*i+3*j+1], pos[9*i+3*j+2]);
     vec3.set(v[j], vbodata[3*stride*i+stride*j], vbodata[3*stride*i+stride*j+1], vbodata[3*stride*i+stride*j+2]);
     }

     vec3.sub(e1, v[1], v[0]);
     vec3.sub(e2, v[2], v[0]);

     vec3.cross(nn, e1, e2);
     vec3.normalize(n, nn);
     for ( let j = 0; j < 3; j++ )
     {
     vbodata[3*stride*i+stride*j+3] = n[0];
     vbodata[3*stride*i+stride*j+4] = n[1];
     vbodata[3*stride*i+stride*j+5] = n[2];
     }
     }
     meshdata.vertex = vbodata; */

    if (mesh.primitives[0].indices && mesh.primitives[0].indices.value) {
      this.indexData_ = mesh.primitives[0].indices.value.slice();
    }
    /*
     meshdata.index = new Uint32Array(3*triangles);
     for (let i = 0; i < triangles; i++ )
     {
     meshdata.index[3*i] = 3*i;
     meshdata.index[3*i+1] = 3*i+1;
     meshdata.index[3*i+2] = 3*i+2;
     } */

    positionData = null;
    normalData = null;
    batchData = null;
    gltf = null;
  }
}

export { W3DTile };
